import React, { useState, useMemo } from "react";
import {
  Mail,
  Phone,
  Tag,
  Calendar,
  FileText,
  MessageSquare,
  Pencil,
  UserCheck,
  AlertCircle,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { DonorFlags, InfoLine } from "./DonorParts";
import { QuickLogModal } from "../features/QuickLogModal";
import {
  calculateEngagement,
  getPathwayForScore,
  resolveEffectivePathway,
  getMemberActivities,
} from "../../utils/engagementScoring";

export const DonorCard = ({ member }) => {
  const { palette, navigate, pathways, personas, allGifts, allEventAttendance, allSponsorships } = useApp();
  const hasNotes = member.notes && member.notes.trim().length > 0;
  const hasDirector = member.assignedDirector && member.assignedDirector.trim();
  const logCount = (member.contactLog || []).length;
  const [showLog, setShowLog] = useState(false);

  // Engagement scoring from shared engine (memoized for performance)
  const activities = useMemo(() => getMemberActivities(member.id, allGifts, allEventAttendance, allSponsorships), [member.id, allGifts, allEventAttendance, allSponsorships]);
  const engagement = useMemo(() => calculateEngagement(member, undefined, activities), [member, activities]);
  const engagementPathway = useMemo(() => getPathwayForScore(engagement.score), [engagement.score]);

  // Effective pathway: calculated by default, manual override wins if set
  // Portfolio floor: assigned donors get minimum pathway based on lifetime giving
  const memberLifetimeGiving = useMemo(() => (activities.memberGifts || []).reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0), [activities.memberGifts]);
  const effective = useMemo(() => resolveEffectivePathway(member, engagement.score, pathways, undefined, { lifetimeGiving: memberLifetimeGiving }), [member, engagement.score, pathways, memberLifetimeGiving]);

  // Persona lookup
  const memberPersona = useMemo(() =>
    member.persona && (personas || []).length > 0
      ? personas.find((p) => p.id === member.persona)
      : null, [member.persona, personas]);

  const scoreColor =
    engagement.score >= 80
      ? "#10b981"
      : engagement.score >= 30
      ? "#3b82f6"
      : engagement.score >= 10
      ? "#f59e0b"
      : "#ef4444";

  const daysColor =
    engagement.daysSinceContact === null
      ? "#dc2626"
      : engagement.daysSinceContact <= 14
      ? "#16a34a"
      : engagement.daysSinceContact <= 30
      ? "#65a30d"
      : engagement.daysSinceContact <= 60
      ? "#d97706"
      : "#dc2626";

  return (
    <>
      <Card
        hoverable
        onClick={() => navigate("memberDetail", member)}
        style={{
          padding: 0,
          overflow: "hidden",
          borderLeft: `4px solid ${effective.color}`,
        }}
      >
        {/* Identity Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 16px 10px",
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              flexShrink: 0,
              background: `linear-gradient(135deg, ${effective.color}, ${effective.color}88)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: FONT_SIZES.md,
              fontWeight: 700,
              color: "#fff",
              letterSpacing: "0.5px",
            }}
          >
            {member.donorType === "organization"
              ? (member.organization || "?").substring(0, 2).toUpperCase()
              : `${(member.firstName || "?")[0]}${(member.lastName || "?")[0]}`}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: FONT_SIZES.md,
                fontWeight: 600,
                color: palette.text1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {member.donorType === "organization"
                ? member.organization || "Organization"
                : member.donorType === "household"
                  ? member.householdName || `${member.firstName || ""} ${member.lastName || ""}`
                  : member.credentialedFullName || `${member.firstName || ""} ${member.lastName || ""}`}
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {member.donorType === "organization"
                ? [member.firstName, member.lastName].filter(Boolean).join(" ") || "No contact"
                : member.organization || "No organization"}
            </div>
          </div>
        </div>

        {/* Classification Zone */}
        <div
          style={{
            margin: "0 12px",
            padding: "10px 12px",
            borderRadius: 8,
            background: `${effective.color}08`,
            border: `1px solid ${effective.color}18`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <DonorFlags member={member} effective={effective} small />
              {/* Donor type badge (household/org only) */}
              {member.donorType === "household" && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "2px 6px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    background: "#8b5cf620",
                    color: "#7c3aed",
                    whiteSpace: "nowrap",
                  }}
                >
                  HH
                </span>
              )}
              {member.donorType === "organization" && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "2px 6px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    background: "#f59e0b20",
                    color: "#b45309",
                    whiteSpace: "nowrap",
                  }}
                >
                  Org
                </span>
              )}
              {/* ── Persona Badge (Rule 4: solid bg + white text) ── */}
              {memberPersona && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    background: memberPersona.color || "#6b7280",
                    color: "#ffffff",
                    whiteSpace: "nowrap",
                  }}
                >
                  {memberPersona.name}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {hasNotes && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    fontSize: FONT_SIZES.xs,
                    color: effective.color,
                    fontWeight: 600,
                  }}
                >
                  <FileText size={ICON_SIZES.xs} />
                  <span>Notes</span>
                </div>
              )}
              {logCount > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    fontSize: FONT_SIZES.xs,
                    color: "#3b82f6",
                    fontWeight: 600,
                  }}
                >
                  <MessageSquare size={ICON_SIZES.xs} />
                  <span>{logCount}</span>
                </div>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLog(true);
                }}
                title="Log contact"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  border: `1px solid ${effective.color}40`,
                  background: `${effective.color}10`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = effective.color;
                  e.currentTarget.querySelector("svg").style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `${effective.color}10`;
                  e.currentTarget.querySelector("svg").style.color =
                    effective.color;
                }}
              >
                <Pencil
                  size={ICON_SIZES.xs}
                  style={{ color: effective.color, transition: "color 0.15s" }}
                />
              </button>
            </div>
          </div>
          {hasDirector ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 8,
                fontSize: FONT_SIZES.xs,
                color: effective.color,
                fontWeight: 600,
              }}
            >
              <UserCheck size={ICON_SIZES.xs} />
              {member.assignedDirector}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 8,
                fontSize: FONT_SIZES.xs,
                color: "#d97706",
                fontWeight: 500,
                opacity: 0.8,
              }}
            >
              <AlertCircle size={ICON_SIZES.xs} />
              Unassigned
            </div>
          )}
          {/* Pathway mismatch indicator */}
          {effective.mismatch && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginTop: 6,
                fontSize: 10,
                color: "#d97706",
                fontWeight: 600,
                padding: "3px 8px",
                borderRadius: 4,
                background: "#fffbeb",
                border: "1px solid #fcd34d",
              }}
            >
              <AlertCircle size={ICON_SIZES.xs} />
              <span>
                Set to <strong>{effective.label}</strong> — score says{" "}
                <strong>{effective.calculatedLabel}</strong>
              </span>
            </div>
          )}
        </div>

        {/* ── Engagement Row (NEW) ─────────────────────────────
             Two numbers: engagement score + days since contact.
             Matches what CultivationView pipeline cards show.
             ──────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            margin: "6px 12px 0",
            borderRadius: 6,
            background: `${scoreColor}06`,
            border: `1px solid ${scoreColor}12`,
          }}
        >
          {/* Score + Pathway label */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: scoreColor,
                lineHeight: 1,
              }}
            >
              {engagement.score}
            </span>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: scoreColor,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                {engagementPathway.label}
              </div>
              <div style={{ fontSize: 9, color: "#9ca3af" }}>engagement</div>
            </div>
          </div>

          {/* Days since contact */}
          <div
            style={{
              textAlign: "right",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div>
              <div
                style={{ fontSize: 9, color: "#9ca3af", textAlign: "right" }}
              >
                last contact
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: daysColor,
                  textAlign: "right",
                }}
              >
                {engagement.daysSinceContact === null
                  ? "never"
                  : engagement.daysSinceContact <= 14
                  ? "recent"
                  : engagement.daysSinceContact <= 30
                  ? "okay"
                  : engagement.daysSinceContact <= 60
                  ? "stale"
                  : "overdue"}
              </div>
            </div>
            <span
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: daysColor,
                lineHeight: 1,
              }}
            >
              {engagement.daysSinceContact === null
                ? "—"
                : engagement.daysSinceContact}
            </span>
          </div>
        </div>

        {/* Data Zone */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: "10px 16px 14px",
          }}
        >
          {member.email && <InfoLine icon={Mail} text={member.email} />}
          {member.phone && <InfoLine icon={Phone} text={member.phone} />}
          {member.memberSince && (
            <InfoLine icon={Calendar} text={`Donor since ${member.memberSince}`} />
          )}
          {/* Pathway Bar */}
          <div
            style={{
              padding: "6px 16px",
              background: effective.color,
              color: "#ffffff",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              textAlign: "center",
            }}
          >
            {effective.label}
          </div>
        </div>
      </Card>
      {showLog && (
        <QuickLogModal member={member} onClose={() => setShowLog(false)} />
      )}
    </>
  );
};
